import { apiClient } from "./apiClient.ts";
import { User } from "../stores/types";

// Types for the new multi-step login flow
interface CheckPhoneRequest {
  phone: string;
}

interface CheckPhoneResponse {
  is_root: boolean;
  kindergartens: Array<{
    kindergarten_id: string;
    kindergarten_name: string;
    role: string;
  }>;
}

interface LoginRequest {
  phone: string;
  password: string;
  kindergarten_id?: string;
  /** Client marker so the backend can stamp dashboard-specific login time. */
  client?: string;
}

interface KindergartenSettings {
  name: string;
  name_ar: string | null;
  name_en: string | null;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  opening_time: string;
  closing_time: string;
  phone: string | null;
  email: string | null;
  logo_url: string | null;
  billing_enabled: boolean;
  /**
   * Financial Management module flag (root-admin gated server-side).
   * Defaults false for logins predating the module.
   */
  finance_enabled?: boolean;
  /** School default currency for the finance module (set once, immutable). */
  finance_default_currency?: string | null;
  /**
   * Optional sections inside finance, each switchable per school by a root
   * admin. A missing value means ON (logins predating the switches).
   */
  finance_expenses_enabled?: boolean;
  finance_payroll_enabled?: boolean;
  finance_reports_enabled?: boolean;
  finance_exports_enabled?: boolean;
  finance_parent_app_enabled?: boolean;
  /** Present for kindergarten_admin login; omitted for other roles. */
  auto_include_new_children?: boolean;
  /** IANA zone for display (e.g. Asia/Hebron). Always prefer this over device TZ. */
  timezone?: string;
  /**
   * Institution type driving user-facing naming (school, kindergarten,
   * summer_camp, course). Sent flat in the login payload; the org-label
   * i18next post-processor reads it. Defaults to "school" if absent.
   */
  management_type?: string;
  /**
   * Journey activity log — per-school, root-admin controlled. Gates the page
   * only: the backend records journey changes for every school regardless, so
   * a school switched on later can still look back. Defaults false for logins
   * predating it.
   */
  journey_activity_log_enabled?: boolean;
  /**
   * Bus Fees module — per-school, root-admin controlled, dashboard-only. A
   * fee list derived from each child's bus arrangement for schools that keep
   * their books elsewhere. Defaults false for logins predating it.
   */
  bus_fees_enabled?: boolean;
  /**
   * Riders who track their own bus «الركاب» — per-school, root-admin
   * controlled. Brings the Employees page's «الركاب» tab; gates *adding* a
   * rider only, never one already riding. Defaults false for logins predating
   * it (`isRidersEnabled()`).
   */
  riders_enabled?: boolean;
  /**
   * Merging two journeys into one — per-school, root-admin controlled. Brings
   * "Merge with another journey" to the journeys table and the journey page.
   * Defaults false for logins predating it (`isJourneyMergeEnabled()`).
   */
  journey_merge_enabled?: boolean;
  /**
   * Polls «الاستطلاعات» — a Wosool service the school admin switches once
   * Wosool opens it. Brings the Polls page (`/manager/polls`); the app reads
   * the switch itself through `GET /polls/feed`. Defaults false for logins
   * predating it (`isPollsEnabled()`).
   */
  polls_enabled?: boolean;
  /**
   * Parent → school requests — the school's own switch. Defaults false for
   * logins predating it.
   */
  parent_requests_enabled?: boolean;
  /**
   * Parent → bus supervisor requests — the school's own switch, separate from
   * the one above. The school reads these (read-only) but is never notified
   * of them. Defaults false for logins predating it.
   */
  supervisor_requests_enabled?: boolean;
  /**
   * Show bus supervisors what became of their bell — the school's own switch,
   * off by default. The school's staff see bells on the run page only when it
   * is on; root always does. Defaults false for logins predating it.
   */
  bell_status_enabled?: boolean;
  /**
   * Bus moves — children riding another bus, for one ride or for good. The
   * school's own switch; gates making a move, never one already in force.
   * Defaults false for logins predating it.
   */
  child_moves_enabled?: boolean;
  /**
   * Education (التعليم) — per-school, root-admin controlled. Brings the
   * Education pages, and with them Fosool «فصول» and the Wosool / Fosool
   * switch (`useManagerRoutes`). Read at login only; defaults false for
   * logins predating it.
   */
  education_enabled?: boolean;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
  user_role: string;
  user: User;
  kindergarten_settings: KindergartenSettings | null;
}

const authService = {
  /**
   * Checks phone number to get associated kindergartens
   */
  checkPhone: (phone: string) => {
    return apiClient.post<CheckPhoneResponse>(
      "/api/v1/auth/check_phone_number",
      {
        phone,
        // The dashboard marker: only the web dashboard is offered accountant
        // accounts (the mobile app sends none and would open them as parents).
        client: "dashboard",
      }
    );
  },

  /**
   * Handles user login with the new multi-step login flow
   * @param phone The user's phone number
   * @param password The user's password
   * @param kindergartenId Optional kindergarten ID for multi-tenancy
   */
  login: (phone: string, password: string, kindergartenId?: string) => {
    const payload: LoginRequest = {
      phone,
      password,
      client: "dashboard",
    };

    if (kindergartenId) {
      payload.kindergarten_id = kindergartenId;
    }

    return apiClient.post<LoginResponse>("/api/v1/auth/login", payload, true);
  },

  getMe: () => {
    return apiClient.get<User>("/api/v1/user/me");
  },

  /**
   * Logout the current user and revoke all device tokens
   */
  logout: () => {
    return apiClient.post<{
      message: string;
      devices_revoked: number;
      logout_timestamp: string;
    }>("/api/v1/auth/logout", {});
  },
};

export default { authService };
export type { LoginResponse, KindergartenSettings };
